import { fmtReq } from './format';
import { pluralUnit } from './pricing';
import { m } from '../paraglide/messages';

/** How a billable unit reads in the page language: after a rate, beside a
 *  count, and as a plural noun in a sentence. */
interface UnitText {
  per: () => string;
  count: (n: number) => string;
  plural: () => string;
}

type CountMessage = (inputs: { count: string }) => string;

const unitText = (per: () => string, one: CountMessage, other: CountMessage, plural: () => string): UnitText => ({
  per,
  plural,
  count: (n) => (n === 1 ? one : other)({ count: fmtReq(n) }),
});

/** The published billing units. A unit the gateway adds later reads in English
 *  until it is listed, as `quotedRateLabel` does. */
const UNIT_TEXT: Record<string, UnitText> = {
  request: unitText(m.measure_unit_request_per, m.measure_unit_request_one, m.measure_unit_request_other, m.measure_unit_request_plural),
  result: unitText(m.measure_unit_result_per, m.measure_unit_result_one, m.measure_unit_result_other, m.measure_unit_result_plural),
  keyword: unitText(m.measure_unit_keyword_per, m.measure_unit_keyword_one, m.measure_unit_keyword_other, m.measure_unit_keyword_plural),
  'audio minute': unitText(
    m.measure_unit_audio_minute_per,
    m.measure_unit_audio_minute_one,
    m.measure_unit_audio_minute_other,
    m.measure_unit_audio_minute_plural,
  ),
  asin: unitText(m.measure_unit_asin_per, m.measure_unit_asin_one, m.measure_unit_asin_other, m.measure_unit_asin_plural),
  profile: unitText(m.measure_unit_profile_per, m.measure_unit_profile_one, m.measure_unit_profile_other, m.measure_unit_profile_plural),
};

const englishUnit = (unit: string): UnitText => ({
  per: () => `per ${unit}`,
  count: (n) => `${n.toLocaleString('en-US')} ${n === 1 ? unit : pluralUnit(unit)}`,
  plural: () => pluralUnit(unit),
});

/** A billing unit in the page language, e.g. `per keyword`, `3 keywords`, `keywords`. */
export const billingUnitText = (unit: string): UnitText => UNIT_TEXT[unit] ?? englishUnit(unit);
